import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Button, CloseButton, Group, Menu, Popover, ScrollArea, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconDots, IconPlus, IconSearch, IconTrash, IconX } from '@tabler/icons-react';
import type { TagDTO } from '@/types/messageTemplates';
import styles from './TagFilters.module.sass';

const SEARCH_ICON_SIZE = 16;

interface TagFiltersProps {
  tags: TagDTO[];
  selectedTagIds: string[];
  total: number;
  tagCountMap: Map<string, number>;
  onTagClick: (tagId: string | null) => void;
  onAddTag: (name: string) => void;
  onDeleteTag: (tagId: string) => void;
  disabled?: boolean;
}

export const TagFilters = ({
  tags,
  selectedTagIds,
  total,
  tagCountMap,
  onTagClick,
  onAddTag,
  onDeleteTag,
  disabled = false,
}: TagFiltersProps) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const handleSearchToggle = () => {
    if (isSearchOpen) {
      setSearchQuery('');
    }
    setIsSearchOpen((prev) => !prev);
  };

  const handleAddSubmit = () => {
    const trimmed = newTagName.trim();
    if (!trimmed) return;
    onAddTag(trimmed);
    setNewTagName('');
    setIsAddMenuOpen(false);
  };

  const handleDeleteTag = (tagId: string) => {
    const usageCount = tagCountMap.get(tagId) ?? 0;
    if (usageCount > 0) {
      showNotification({
        title: 'Could not delete tag',
        message: 'Tag is in use and cannot be deleted',
        color: 'yellow',
        autoClose: 4000,
        withCloseButton: true,
      });
      return;
    }
    onDeleteTag(tagId);
  };

  const filteredTags = searchQuery
    ? tags.filter((tag) => tag.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : tags;

  return (
    <ScrollArea type="hover" scrollbarSize={4} offsetScrollbars="x">
      <Group gap="xs" wrap="nowrap" align="center" className={disabled ? styles.disabled : undefined}>
        {isSearchOpen ? (
          <TextInput
            ref={searchInputRef}
            size="xs"
            placeholder="Search tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftSection={<IconSearch className={styles.searchIcon} size={SEARCH_ICON_SIZE} />}
            rightSection={<CloseButton size="xs" onClick={handleSearchToggle} disabled={disabled} />}
            radius="xl"
            className={styles.searchInput}
            disabled={disabled}
          />
        ) : (
          <ActionIcon
            variant="default"
            radius="xl"
            size="lg"
            onClick={handleSearchToggle}
            className={styles.noShrink}
            disabled={disabled}
          >
            <IconSearch className={styles.searchIcon} size={SEARCH_ICON_SIZE} />
          </ActionIcon>
        )}

        <Popover opened={isAddMenuOpen} onChange={setIsAddMenuOpen} position="bottom-start" shadow="md" withArrow>
          <Popover.Target>
            <Button
              variant="filled"
              size="xs"
              radius="xl"
              leftSection={<IconPlus size={14} />}
              onClick={() => setIsAddMenuOpen((prev) => !prev)}
              className={styles.noShrink}
              disabled={disabled}
              loading={disabled}
            >
              Add Tag
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            <Group gap="xs" wrap="nowrap">
              <TextInput
                size="xs"
                placeholder="Tag name"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                radius="xl"
              />
              <Button size="xs" radius="xl" leftSection={<IconPlus size={14} />} onClick={handleAddSubmit}>
                Add
              </Button>
              <ActionIcon size="md" radius="xl" variant="subtle" color="gray" onClick={() => setIsAddMenuOpen(false)}>
                <IconX size={16} />
              </ActionIcon>
            </Group>
          </Popover.Dropdown>
        </Popover>

        <button
          type="button"
          className={`${styles.tagChip} ${selectedTagIds.length === 0 ? styles.tagChipActive : ''} ${disabled ? styles.tagChipDisabled : ''}`}
          onClick={() => onTagClick(null)}
          disabled={disabled}
        >
          All ({total})
        </button>

        {filteredTags.map((tag) => (
          <div key={tag.id} className={styles.tagChipWrapper}>
            <button
              type="button"
              className={`${styles.tagChip} ${styles.tagChipWithMenu} ${selectedTagIds.includes(tag.id) ? styles.tagChipActive : ''} ${disabled ? styles.tagChipDisabled : ''}`}
              onClick={() => onTagClick(tag.id)}
              disabled={disabled}
            >
              {tag.name} ({tagCountMap.get(tag.id) ?? 0})
            </button>
            <Menu position="bottom-start" shadow="sm" withinPortal>
              <Menu.Target>
                <ActionIcon
                  variant="transparent"
                  size="xs"
                  className={styles.tagDotsButton}
                  onClick={(e) => e.stopPropagation()}
                  disabled={disabled}
                >
                  <IconDots size={14} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => handleDeleteTag(tag.id)}>
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        ))}
      </Group>
    </ScrollArea>
  );
};
